import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdminGetProducts } from '@/api/auth/admin/admin';
import { AddProductDialog } from '@/components/admin/AddProductDialog';
import { useNavigate } from 'react-router-dom';

/**
 * Admin products page for managing products
 */
export default function AdminProductsPage() {
  const navigate = useNavigate();

  // Fetch all products
  const {
    data: productsData,
    isLoading: isLoadingProducts,
    error: productsError,
  } = useAdminGetProducts();

  const products = productsData?.data?.products || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Products</h1>
          <p className="text-muted-foreground">
            Manage products and their configurations
          </p>
        </div>
        <AddProductDialog />
      </div>

      {productsError ? (
        <div className="text-center text-destructive py-8">
          <p>Failed to load products: {(productsError as any)?.message || 'Unknown error'}</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product ID</TableHead>
              <TableHead>Default Entitlement</TableHead>
              <TableHead>Trials Count</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingProducts ? (
              [...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton className="h-4 w-[200px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-[80px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-[60px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-8 w-[80px]" />
                  </TableCell>
                </TableRow>
              ))
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  No products found. Create your first product to get started.
                </TableCell>
              </TableRow>
            ) : (
              products.map((product) => (
                <TableRow key={product.productId}>
                  <TableCell className="font-medium font-mono">
                    {product.productId}
                  </TableCell>
                  <TableCell>
                    {product.hasDefaultEntitlement ? (
                      <Badge variant="default">Configured</Badge>
                    ) : (
                      <Badge variant="outline">Not Set</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {product.trialsCount || 0}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/dashboard/admin/products/${product.productId}`)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
